import { useEffect } from 'react'
import { createPortal } from 'react-dom'

interface LogoutConfirmModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
}

export function LogoutConfirmModal({ open, onClose, onConfirm }: LogoutConfirmModalProps) {
  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) {
    return null
  }

  const modal = (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-[6px]"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-confirm-title"
        aria-describedby="logout-confirm-description"
        className="w-full max-w-[430px] rounded-[24px] bg-white px-6 py-8 text-center shadow-[0_30px_80px_rgba(15,23,42,0.25)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 id="logout-confirm-title" className="text-[22px] font-semibold tracking-[-0.01em] text-[#18212f]">
          Are you sure you want to log out?
        </h2>

        <p id="logout-confirm-description" className="mt-3 text-[15px] leading-6 text-[#6b7280]">
          You can log in again anytime.
        </p>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-12 items-center justify-center rounded-[12px] border border-[#d8dce2] bg-white px-4 text-[15px] font-medium text-[#374151] transition hover:bg-[#f8fafc]"
          >
            No
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-12 items-center justify-center rounded-[12px] bg-[#0d6064] px-4 text-[15px] font-medium text-white transition hover:bg-[#0b5255]"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  )

  return typeof document === 'undefined' ? modal : createPortal(modal, document.body)
}